import React from 'react';

import PageHero from '../components/PageHero';
import SectionHeader from '../components/SectionHeader';
import WorkAvatar from '../components/WorkAvatar';
import '../styles/pages/work.css';

const BOOK_DEMO = 'https://calendar.app.google/6g62S4xtRa8eWipt5';

const caseStudies = [
  {
    id: 1,
    headline: "95% Call Response Rate",
    client: "Dr. William July",
    title: "Clinical Psychology Practice",
    industry: "Healthcare",
    challenge: "High call volumes were overwhelming staff and causing missed client connections — patients seeking urgent mental health support couldn't get through during peak hours.",
    solution: "Deployed Digital Front Desk voice assistant with 24/7 availability",
    results: [
      "95% call response rate (up from ~40%)",
      "15+ hours saved weekly on front-desk tasks",
      "60% reduction in missed appointments",
      "Staff refocused on patient care instead of phone triage"
    ],
    technologies: ["Power Platform", "Azure AI Speech", "Dataverse", "Power Automate"],
    timeline: "3 months",
    image: "/images/testimonials/william-july.webp",
    testimonial: "The AI voice assistant has transformed how we handle client communications. Our staff can now focus on patient care instead of answering repetitive calls.",
    details: "We implemented a comprehensive AI voice assistant that handles initial intake, appointment scheduling, and basic inquiries while seamlessly transferring complex cases to human staff. The system operates 24/7, ensuring no client call goes unanswered — even outside business hours."
  },
  {
    id: 2,
    headline: "80% Scheduling Automation",
    client: "Gary Kenning",
    title: "Managed ISP & Network Services",
    industry: "Technology",
    challenge: "Manual client communication and scheduling processes were creating bottlenecks that limited the company's ability to scale.",
    solution: "Implemented AI voice assistant and end-to-end workflow automation",
    results: [
      "80% reduction in manual scheduling tasks",
      "Sub-minute response time to client inquiries",
      "Automated project onboarding from days to hours",
      "Scaled operations without adding admin headcount"
    ],
    technologies: ["Power Apps", "Power Automate", "Azure Functions", "Microsoft Graph"],
    timeline: "2 months",
    image: "/images/testimonials/gary-kenning.webp",
    testimonial: "The automation solution has allowed us to scale our operations without proportionally increasing our administrative overhead.",
    details: "We developed an integrated solution that automates scheduling, client onboarding, and follow-up communications — enabling the team to handle 3x the client volume with the same staff while maintaining the personal touch that sets their service apart."
  },
  {
    id: 3,
    headline: "80% Faster Intake, Multi-Language",
    client: "Maral Aineh Beigi",
    title: "Financial Advisory Practice",
    industry: "Financial Services",
    challenge: "Manual intake processes and language barriers were limiting the practice's ability to serve a diverse, multilingual client base.",
    solution: "Multi-lingual Digital Front Desk to automate client intake and outreach in multiple languages",
    results: [
      "80% reduction in manual intake processing time",
      "Automated intake available in multiple languages",
      "Expanded addressable client base across language groups",
      "Consistent, compliant communication across all touchpoints"
    ],
    technologies: ["Digital Front Desk", "Power Platform", "Azure AI Speech", "Multi-language Support"],
    timeline: "4 months",
    image: "/images/testimonials/maral-aineh-beigi.webp",
    testimonial: "The multi-lingual Digital Front Desk has transformed how I connect with clients from diverse backgrounds. I can now serve clients in their preferred language while automating my entire intake process.",
    details: "We implemented a multi-lingual Digital Front Desk that automates client intake, handles initial consultations in multiple languages, and streamlines outreach — allowing the practice to serve non-English speaking clients without additional staff or manual translation."
  }
];

// The Digital Front Desk customers quoted on digitalfrontdesk.ca, word for word,
// so both sites carry every testimonial. The court clinic's entry is its own
// public LinkedIn announcement, linked to the original.
const testimonials = [
  {
    name: 'Joelene Bamford',
    role: 'Executive Director, London Family Court Clinic',
    image: '/images/testimonials/joelene-bamford.webp',
    text: 'Our experience with Digital Front Desk has been exceptional, from the ease of implementing the system to the outstanding responsiveness and support we received throughout the process. We are especially pleased to partner with a Canadian company that genuinely took the time to understand our needs and provide a solution that works for our organization.'
  },
  {
    name: 'Carmen Harris',
    role: 'Independent Insurance Coordinator',
    image: '/images/testimonials/carmen-harris.webp',
    text: 'Carla makes it easy to leave a message and truly feels like speaking with a person. The process is smooth and welcoming, which is important for our mental-health clients. I also appreciate that Carla handles many routine caller questions and provides accurate information right away. This gives our staff more time to focus on patient care instead of managing phone calls.'
  },
  {
    name: 'London Family Court Clinic',
    role: 'Announcement on LinkedIn · September 16, 2026',
    image: '/images/testimonials/lfcc-logo.webp',
    logo: true,
    paragraphs: [
      'We are pleased to introduce a new way to welcome clients and visitors to our office!',
      'Our new digital visitor assistant makes checking-in quick and simple. When you arrive, the virtual assistant will ask two simple questions let your appointment provider know you’re here.',
      'We’re excited to modernize our reception process while continuing to make sure clients and visitors feel welcomed and know what to expect when they arrive.',
      'We’re grateful to offer this new service in partnership with Digital Front Desk by Moneli Automation, a Canadian company, who have been incredibly supportive and responsive throughout the process!'
    ],
    link: 'https://www.linkedin.com/posts/digitalfrontdesk-clientexperience-londonont-ugcPost-7506038266815643648-ig7K/'
  }
];

const impact = [
  { value: '80%', label: 'Time Saved' },
  { value: '95%', label: 'Call Response' },
  { value: '70%', label: 'Process Speed' },
  { value: '24/7', label: 'Availability' }
];

const trackRecord = [
  { icon: 'fa-rocket', count: 50, suffix: '+', label: 'Projects Delivered', note: 'Successfully deployed automation solutions' },
  { icon: 'fa-industry', count: 3, suffix: '', label: 'Industries Served', note: 'Healthcare, Technology, Financial' },
  { icon: 'fa-star', count: 95, suffix: '%', label: 'Client Satisfaction', note: 'Consistently exceeding expectations' },
  { icon: 'fa-clock', count: 20, suffix: '+', label: 'Hours Saved Weekly', note: 'Average time savings per client' }
];

const products = [
  {
    name: 'Digital Front Desk',
    text: 'A digital assistant that handles calls & SMS, a kiosk that manages check-ins, and an online chatbot — with everything tracked automatically.',
    href: 'https://digitalfrontdesk.ca/',
    cta: 'Visit Digital Front Desk',
    icon: 'fa-headset',
    external: true
  },
  {
    name: 'My Clinical Writer',
    text: 'AI-powered platform that turns patient interviews into clear clinical reports. Save hours of documentation while staying accurate and compliant.',
    href: 'https://myclinicalwriter.ai/',
    cta: 'Visit My Clinical Writer',
    icon: 'fa-file-medical',
    external: true
  },
  {
    name: 'Student Housing Management',
    text: 'A student housing operations portal for applications and waitlists, offers and e-signatures, room assignments, roommate groups, work orders, communications, and reporting.',
    href: '/case-studies/housing',
    cta: 'View Student Housing Management',
    icon: 'fa-building-user'
  },
  {
    name: 'AI Ethics Education Platform',
    text: 'A professional development course on ethical AI in clinical documentation. Includes modules, assessments, and verifiable certificates.',
    href: '/case-studies/ethics',
    cta: 'View AI Ethics Course',
    icon: 'fa-graduation-cap'
  },
  {
    name: 'DinnerSolved',
    text: 'A pickup-first SaaS marketplace connecting local kitchens with busy households for pre-ordered, batch-prepared family dinners — built on Azure with a scalable foundation for AI-driven personalization.',
    href: 'https://dinnersolved.ca/',
    cta: 'Visit DinnerSolved',
    icon: 'fa-utensils',
    external: true
  }
];

const process = [
  { icon: 'fa-search', title: 'Discovery', text: 'Deep dive into your current processes, pain points, and automation opportunities', time: '1-2 weeks' },
  { icon: 'fa-drafting-compass', title: 'Design', text: 'Create tailored solution architecture with detailed wireframes and technical specifications', time: '2-3 weeks' },
  { icon: 'fa-code', title: 'Development', text: 'Build, test, and refine your automation solution with regular progress updates', time: '4-8 weeks' },
  { icon: 'fa-rocket', title: 'Deployment', text: 'Launch your system with training, documentation, and ongoing support', time: '1-2 weeks' }
];

const industries = [
  { icon: 'fa-notes-medical', label: 'Healthcare' },
  { icon: 'fa-laptop-code', label: 'Technology' },
  { icon: 'fa-chart-line', label: 'Financial Services' },
  { icon: 'fa-building', label: 'Professional Services' }
];

const pad = (n) => String(n).padStart(2, '0');

function CaseStudies() {
  return (
    <div className="case-studies-page work-page">
      {/* HERO */}
      <PageHero
        variant="dark"
        eyebrow="Proven Results"
        title={<>Success stories that <em>inspire</em></>}
        lead="Discover how our AI automation solutions have transformed operations across healthcare, technology, and financial services. Real clients, real results, real impact."
        crumbs={[{ to: '/', label: 'Home' }, { label: 'Case Studies' }]}
        actions={<>
          <a href={BOOK_DEMO} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-lg" data-magnetic>
            Book a Demo
          </a>
          <a href="#case-studies" className="btn btn-outline-light btn-lg">
            View Case Studies <i className="fa-solid fa-arrow-down" aria-hidden="true" />
          </a>
        </>}
      >
        <div className="work-impact" aria-label="Impact overview">
          <p className="work-impact__label">
            <i className="fa-solid fa-chart-line" aria-hidden="true" /> Impact Overview
          </p>
          <ul className="work-impact__grid" data-reveal-stagger="0.1">
            {impact.map((item) => (
              <li className="work-impact__item" key={item.label}>
                <span className="work-impact__value">{item.value}</span>
                <span className="work-impact__name">{item.label}</span>
              </li>
            ))}
          </ul>
        </div>
      </PageHero>

      {/* TRACK RECORD */}
      <section className="section section--light" aria-labelledby="track-title">
        <div className="container">
          <SectionHeader
            index="01"
            eyebrow="Proven Excellence"
            title={<>Our track <em>record</em></>}
            lead="Delivering measurable results across diverse industries."
            id="track-title"
          />
          <div className="stats work-stats" data-reveal-stagger="0.1">
            {trackRecord.map((s) => (
              <div className="stat" key={s.label}>
                <span className="icon-tile icon-tile--sm work-stats__icon">
                  <i className={`fa-solid ${s.icon}`} aria-hidden="true" />
                </span>
                <p className="stat__num">
                  <span data-count={s.count}>{s.count}</span>
                  {s.suffix && <span className="stat__suffix">{s.suffix}</span>}
                </p>
                <p className="work-stats__title">{s.label}</p>
                <p className="stat__label">{s.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CASE STUDIES */}
      <section id="case-studies" className="section section--sand work-anchor" aria-labelledby="stories-title">
        <div className="container">
          <SectionHeader
            index="02"
            eyebrow="Real Impact, Real Results"
            title={<>Client success <em>stories</em></>}
            lead="Deep dive into how we've transformed operations for our clients."
            id="stories-title"
          />

          <div className="work-cases">
            {caseStudies.map((study, index) => (
              <article className="work-case" key={study.id} aria-labelledby={`case-${study.id}-title`}>
                <div className="work-case__aside">
                  <div className="work-case__sticky">
                    <p className="work-case__index" aria-hidden="true">{pad(index + 1)}</p>
                    <div className="work-case__meta" data-reveal>
                      <span className="badge-soft">{study.industry}</span>
                      <span className="work-case__time">
                        <i className="fa-solid fa-clock" aria-hidden="true" /> Delivered in {study.timeline}
                      </span>
                    </div>
                    <h3 className="work-case__headline" id={`case-${study.id}-title`} data-split>{study.headline}</h3>
                    <p className="work-case__client-type" data-reveal>{study.title}</p>

                    <figure className="work-quote" data-reveal data-delay="0.1">
                      <i className="fa-solid fa-quote-left work-quote__mark" aria-hidden="true" />
                      <blockquote className="work-quote__text">"{study.testimonial}"</blockquote>
                      <figcaption className="work-quote__by">
                        <WorkAvatar src={study.image} name={study.client} />
                        <span>
                          <strong>{study.client}</strong>
                          <small>{study.title}</small>
                        </span>
                      </figcaption>
                    </figure>
                  </div>
                </div>

                <div className="work-case__body">
                  <div className="work-case__block" data-reveal>
                    <p className="work-case__label">
                      <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" /> Challenge
                    </p>
                    <p className="work-case__lead">{study.challenge}</p>
                  </div>

                  <div className="work-case__block" data-reveal>
                    <p className="work-case__label">
                      <i className="fa-solid fa-lightbulb" aria-hidden="true" /> Solution
                    </p>
                    <p className="work-case__lead">{study.solution}</p>
                    <p className="work-case__text">{study.details}</p>
                  </div>

                  <div className="work-case__block">
                    <p className="work-case__label" data-reveal>
                      <i className="fa-solid fa-chart-line" aria-hidden="true" /> Key Results
                    </p>
                    <ul className="work-results" data-reveal-stagger>
                      {study.results.map((result) => (
                        <li key={result}>
                          <i className="fa-solid fa-check" aria-hidden="true" />
                          <span>{result}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="work-case__block" data-reveal>
                    <p className="work-case__label">
                      <i className="fa-solid fa-layer-group" aria-hidden="true" /> Technologies
                    </p>
                    <ul className="work-tags">
                      {study.technologies.map((tech) => <li key={tech}>{tech}</li>)}
                    </ul>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section section--light" aria-labelledby="voices-title">
        <div className="container">
          <SectionHeader
            index="03"
            eyebrow="In their words"
            title={<>What our clients <em>say</em></>}
            lead="Organisations running Digital Front Desk, in their own words."
            id="voices-title"
          />
          <div className="work-voices" data-reveal-stagger="0.1">
            {testimonials.map((t) => (
              <figure className={`work-quote work-voice${t.paragraphs ? ' work-voice--post' : ''}`} key={t.name}>
                <i className="fa-solid fa-quote-left work-quote__mark" aria-hidden="true" />
                {t.paragraphs ? (
                  <blockquote className="work-voice__post">
                    {t.paragraphs.map((para) => <p key={para}>{para}</p>)}
                  </blockquote>
                ) : (
                  <blockquote className="work-quote__text">"{t.text}"</blockquote>
                )}
                <figcaption className="work-quote__by">
                  <WorkAvatar src={t.image} name={t.name} logo={t.logo} />
                  <span>
                    <strong>{t.name}</strong>
                    <small>{t.role}</small>
                  </span>
                </figcaption>
                {t.link && (
                  <a className="link-arrow work-voice__link" href={t.link} target="_blank" rel="noopener noreferrer">
                    Read the post on LinkedIn <i className="fa-solid fa-arrow-up-right-from-square" aria-hidden="true" />
                  </a>
                )}
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="section section--dark grain" aria-labelledby="products-title">
        <div className="container">
          <SectionHeader
            index="04"
            eyebrow="Practical, deployable solutions"
            title={<>Featured <em>products</em></>}
            lead="Battle-tested tools we deploy and customize for real-world operations."
            id="products-title"
          />
          <div className="work-products" data-reveal-stagger="0.1">
            {products.map((p, i) => (
              <article className={`card card--dark card--num work-product stretch-host${i === 0 ? ' work-product--wide' : ''}`} data-num={pad(i + 1)} key={p.name}>
                <span className="icon-tile">
                  <i className={`fa-solid ${p.icon}`} aria-hidden="true" />
                </span>
                <h3 className="card__title">{p.name}</h3>
                <p className="card__text">{p.text}</p>
                <div className="card__foot">
                  <a
                    className="link-arrow link-stretch"
                    href={p.href}
                    {...(p.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                  >
                    {p.cta}{' '}
                    <i className={`fa-solid ${p.external ? 'fa-arrow-up-right-from-square' : 'fa-arrow-right'}`} aria-hidden="true" />
                  </a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="section section--light" aria-labelledby="process-title">
        <div className="container">
          <div className="work-process">
            <div className="work-process__head">
              <div className="work-process__sticky">
                <SectionHeader
                  index="05"
                  eyebrow="Streamlined & Effective"
                  title={<>Our proven <em>process</em></>}
                  lead="How we deliver successful automation solutions, every time."
                  align="stack"
                  id="process-title"
                />
                <div className="work-total" data-reveal data-delay="0.2">
                  <span className="icon-tile icon-tile--note">
                    <i className="fa-solid fa-clock" aria-hidden="true" />
                  </span>
                  <div>
                    <p className="work-total__title">Total Timeline</p>
                    <p className="work-total__text">Typically 8-15 weeks from start to deployment</p>
                  </div>
                </div>
              </div>
            </div>

            <ol className="work-steps" data-reveal-stagger="0.12">
              {process.map((step, i) => (
                <li className="work-step" key={step.title}>
                  <span className="work-step__num">{pad(i + 1)}</span>
                  <span className="icon-tile work-step__icon">
                    <i className={`fa-solid ${step.icon}`} aria-hidden="true" />
                  </span>
                  <div className="work-step__body">
                    <h3 className="work-step__title">{step.title}</h3>
                    <p className="work-step__text">{step.text}</p>
                  </div>
                  <span className="pill work-step__time">{step.time}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* CLOSING (light: the footer opens with the dark CTA band) */}
      <section className="section section--sand work-close" aria-labelledby="close-title">
        <div className="container">
          <div className="work-close__card">
            <div className="work-close__copy">
              <p className="eyebrow" data-reveal>Start Your Success Story</p>
              <h2 className="h2 work-close__title" id="close-title" data-split>
                Ready to join our success <em>stories?</em>
              </h2>
              <p className="lead" data-reveal data-delay="0.15">
                Transform your operations with AI automation. Let's discuss how we can deliver measurable
                results for your business, just like we did for our other clients.
              </p>
              <div className="btn-group-m work-close__actions" data-reveal data-delay="0.25">
                <a href={BOOK_DEMO} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-lg" data-magnetic>
                  Book a Demo
                </a>
                <a href="/services" className="btn btn-ghost btn-lg">
                  Explore Our Services <i className="fa-solid fa-arrow-right" aria-hidden="true" />
                </a>
              </div>
              <ul className="ticks ticks--inline" data-reveal data-delay="0.35">
                <li>Free initial consultation</li>
                <li>No long-term commitments</li>
                <li>Proven track record</li>
              </ul>
            </div>

            <div className="work-close__badge-wrap" data-reveal="scale" data-delay="0.2">
              <div className="work-close__badge card--note">
                <i className="fa-solid fa-handshake" aria-hidden="true" />
                <p className="work-close__badge-num">95% Success Rate</p>
                <p className="work-close__badge-text">Client satisfaction guaranteed</p>
              </div>
            </div>
          </div>

          <div className="work-trust" data-reveal>
            <p className="work-trust__label">Trusted by professionals across industries</p>
            <ul className="work-trust__list">
              {industries.map((ind) => (
                <li key={ind.label}>
                  <i className={`fa-solid ${ind.icon}`} aria-hidden="true" /> {ind.label}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}

export default CaseStudies;
